import {useEffect} from "react";
import {useTranslation} from "react-i18next";
import {Link, Outlet, useLocation, useParams} from "react-router-dom";
import {useAtomValue, useSetAtom} from "jotai";
import {Divider, Stack, Tab, Tabs, Typography} from "@mui/material";
import OrderIcon from "@mui/icons-material/ShoppingCartOutlined";
import {AppLayout} from "../AppLayout";
import {MenuItem} from "../../types/menuItem";
import {routerCompanyIdAtom, routerOrderIdAtom} from "../../store/routerStore";
import {AuthorizationGuard} from "../AuthorizationGuard";
import {UserGlobalRole} from "../../types/domain/user";
import {activeOrderLoadable} from "../../store/activeOrderStore";

export const OrderPage: React.FC = () => {
    useRouterParamsStoreSync();

    return (
        <AuthorizationGuard permitAnyOfGlobalRoles={[UserGlobalRole.ADMIN, UserGlobalRole.CS, UserGlobalRole.HYDROSAT]}>
            <AppLayout sidePanel={<OrderPagePanel />}>
                <Outlet />
            </AppLayout>
        </AuthorizationGuard>
    );
};

const OrderPagePanel: React.FC = () => {
    const {t} = useTranslation();
    const {companyId, orderId} = useParams();
    const pathname = useLocation().pathname;
    const orderState = useAtomValue(activeOrderLoadable);
    const order = orderState.state === "hasData" && orderState.data !== undefined ? orderState.data : undefined;

    return (
        <Stack direction="column" sx={{height: "calc(100% - 2.25rem)", padding: "1.125rem"}} overflow="hidden">
            <Typography textTransform="uppercase" sx={{color: "#737990", my: "0.5rem"}}>
                {t("order_page.title")}
            </Typography>
            <Divider />
            {companyId && orderId && order && (
                <Stack
                    direction="column"
                    sx={{
                        mt: "0.5rem",
                        mb: "0.75rem",
                    }}
                >
                    <Stack direction="row" gap={1} minWidth="0" alignItems="center" marginBottom="0.75rem">
                        <OrderIcon
                            sx={{
                                color: "#6476CB",
                                width: "1.2rem",
                                height: "1.2rem",
                            }}
                        />
                        <Typography textOverflow="ellipsis" noWrap>
                            # {order.sid} ({order.id.slice(0, 8)})
                        </Typography>
                    </Stack>
                    <Tabs
                        value={pathname}
                        orientation="vertical"
                        textColor="inherit"
                        variant="fullWidth"
                        TabIndicatorProps={{
                            sx: {
                                width: 0,
                            },
                        }}
                        sx={{
                            minHeight: "auto",
                            color: "#000000A0",
                            "& a:hover": {backgroundColor: "#eceff5"},
                            "& .Mui-selected": {backgroundColor: "#f3f5fa"},
                        }}
                    >
                        {menuItems(companyId, orderId).map((item) => (
                            <Tab
                                color="primary"
                                key={item.href}
                                label={t(item.i18nLabel)}
                                icon={item.icon}
                                iconPosition="start"
                                value={item.href}
                                id={item.href}
                                component={Link}
                                to={item.href}
                                sx={{
                                    minHeight: "2.25rem",
                                    height: "2.25rem",
                                    letterSpacing: ".04em",
                                    justifyContent: "left",
                                    whiteSpace: "nowrap",
                                    fontSize: "0.875rem",
                                    opacity: 1,
                                    px: "2rem",
                                    py: "0.5rem",
                                }}
                            />
                        ))}
                    </Tabs>
                </Stack>
            )}
        </Stack>
    );
};

const menuItems = (companyId: string, orderId: string): MenuItem[] => [
    {
        i18nLabel: "order_page.menu.order_details",
        href: `/company/${companyId}/order/${orderId}`,
    },
];

/**
 * Retrieves Router (URL path) params and sets them in Store
 */
const useRouterParamsStoreSync = () => {
    const {orderId, companyId} = useParams();
    const setRouterCompanyId = useSetAtom(routerCompanyIdAtom);
    const setRouterOrderId = useSetAtom(routerOrderIdAtom);

    useEffect(() => {
        setRouterCompanyId(companyId);
        setRouterOrderId(orderId);
    }, [setRouterOrderId, setRouterCompanyId, companyId, orderId]);
};
